import { Link } from "react-router-dom";
import { KeyRound, ShieldAlert, Phone, Mail, MapPin, Car, Info, User, ExternalLink } from "lucide-react";

export default function ClientDossier({ client, canOpenFullRecord = false }) {
    if (!client) return null;
    const rows = [
        client.contact_name && { icon: <User size={14} />, label: "Contact", value: client.contact_name },
        client.contact_phone && { icon: <Phone size={14} />, label: "Phone", value: <a href={`tel:${client.contact_phone}`} className="hover:underline">{client.contact_phone}</a> },
        client.contact_email && { icon: <Mail size={14} />, label: "Email", value: <a href={`mailto:${client.contact_email}`} className="hover:underline">{client.contact_email}</a> },
        client.address && { icon: <MapPin size={14} />, label: "Address", value: client.address },
    ].filter(Boolean);

    const codes = [
        client.gate_code && { icon: <KeyRound size={14} />, label: "Gate code", value: client.gate_code },
        client.alarm_code && { icon: <ShieldAlert size={14} />, label: "Alarm PIN", value: client.alarm_code },
        client.key_location && { icon: <KeyRound size={14} />, label: "Keys", value: client.key_location },
    ].filter(Boolean);

    return (
        <section className="card-flat overflow-hidden" data-testid="client-dossier">
            <div className="h-1.5" style={{ background: client.color || "#1B3C35" }} />
            <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <div className="text-xs uppercase tracking-[0.22em] text-[var(--terracotta)] font-semibold">Client dossier</div>
                        <h3 className="font-display text-xl font-semibold mt-0.5">{client.name}</h3>
                    </div>
                    {canOpenFullRecord && (
                        <Link
                            to={`/clients/${client.id}`}
                            data-testid="open-client-detail"
                            className="btn-ghost inline-flex items-center gap-1.5 text-xs"
                        >
                            <ExternalLink size={12} /> Full record
                        </Link>
                    )}
                </div>

                {rows.length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                        {rows.map((r, i) => (
                            <div key={i} className="flex items-start gap-2">
                                <span className="text-[var(--pine)] mt-0.5">{r.icon}</span>
                                <div className="min-w-0">
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--text-muted)] font-semibold">{r.label}</div>
                                    <div className="break-words">{r.value}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {codes.length > 0 && (
                    <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {codes.map((c, i) => (
                            <div
                                key={i}
                                data-testid={`dossier-code-${c.label.toLowerCase().replace(/\s+/g,"-")}`}
                                className="rounded-xl p-3 border border-[var(--border-soft)]"
                                style={{ background: "var(--pine-light)" }}
                            >
                                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-[var(--pine)] font-semibold">
                                    {c.icon} {c.label}
                                </div>
                                <div className="font-mono text-base font-semibold mt-1 break-all">{c.value}</div>
                            </div>
                        ))}
                    </div>
                )}

                {(client.access_notes || client.parking_notes) && (
                    <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                        {client.access_notes && (
                            <div className="flex items-start gap-2">
                                <span className="text-[var(--pine)] mt-0.5"><Info size={14} /></span>
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--text-muted)] font-semibold">Access</div>
                                    <p className="whitespace-pre-line">{client.access_notes}</p>
                                </div>
                            </div>
                        )}
                        {client.parking_notes && (
                            <div className="flex items-start gap-2">
                                <span className="text-[var(--pine)] mt-0.5"><Car size={14} /></span>
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--text-muted)] font-semibold">Parking</div>
                                    <p className="whitespace-pre-line">{client.parking_notes}</p>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </section>
    );
}
